import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { User, Save, Loader2, Mail, Phone, Building2, Shield, Calendar, Clock, Pencil, FileText } from 'lucide-react';
import HRDocumentsModal from './HRDocumentsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileModal({ open, onClose, token }) {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ full_name: '', phone: '', title: '' });
  const [showHRDocs, setShowHRDocs] = useState(false);

  useEffect(() => {
    if (open && token) {
      setLoading(true);
      setEditing(false);
      axios.get(`${API_URL}/api/users/me/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        if (res.data?.profile) {
          setProfile(res.data.profile);
          setForm({
            full_name: res.data.profile.full_name || '',
            phone: res.data.profile.phone || '',
            title: res.data.profile.title || '',
          });
        }
      }).catch(() => toast.error('Failed to load profile'))
        .finally(() => setLoading(false));
    }
  }, [open, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/users/me/profile`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(prev => ({ ...prev, ...form }));
      toast.success('Profile updated');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-2.5">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-slate-800 mt-0.5">{value || <span className="text-slate-300 italic">Not set</span>}</div>
      </div>
    </div>
  );

  const ROLE_LABELS = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    org_admin: 'Org Admin',
    therapist: 'Therapist',
    staff: 'Staff',
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="profile-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-indigo-600" /> My Profile
          </DialogTitle>
          <DialogDescription>Your personal information and account details.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : profile ? (
          <div className="space-y-1">
            {/* Avatar / Name Header */}
            <div className="flex items-center gap-4 pb-3 border-b border-slate-100">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                {(profile.full_name || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-base font-semibold text-slate-900 truncate" data-testid="profile-name">
                  {profile.full_name || 'No name set'}
                </div>
                <div className="text-sm text-slate-500 truncate">{profile.email}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                    {ROLE_LABELS[profile.role] || profile.role}
                  </Badge>
                  {profile.two_factor_enabled && (
                    <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Shield className="w-3 h-3 mr-1" />2FA
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {editing ? (
              <div className="space-y-3 pt-2">
                <div>
                  <Label htmlFor="prof-name" className="text-sm font-medium">Full Name</Label>
                  <Input
                    id="prof-name"
                    data-testid="profile-edit-name"
                    value={form.full_name}
                    onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="prof-phone" className="text-sm font-medium">Phone</Label>
                  <Input
                    id="prof-phone"
                    data-testid="profile-edit-phone"
                    value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="mt-1"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label htmlFor="prof-title" className="text-sm font-medium">Title</Label>
                  <Input
                    id="prof-title"
                    data-testid="profile-edit-title"
                    value={form.title}
                    onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                    className="mt-1"
                    placeholder="e.g., Licensed Professional Counselor"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={() => setEditing(false)} variant="outline" className="flex-1" data-testid="profile-cancel-btn">
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="profile-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="pt-1">
                <InfoRow icon={Mail} label="Email" value={profile.email} />
                <InfoRow icon={Phone} label="Phone" value={profile.phone} />
                <InfoRow icon={User} label="Title" value={profile.title} />
                <InfoRow icon={Building2} label="Organization" value={profile.organization_name} />
                {profile.npi_number && <InfoRow icon={Shield} label="NPI" value={profile.npi_number} />}
                {profile.license_number && (
                  <InfoRow icon={Shield} label="License" value={`${profile.license_type} #${profile.license_number}`} />
                )}
                <InfoRow icon={Calendar} label="Member Since" value={profile.created_at ? new Date(profile.created_at).toLocaleDateString() : null} />
                <InfoRow icon={Clock} label="Last Login" value={profile.last_login_at ? new Date(profile.last_login_at).toLocaleString() : null} />

                <Button
                  onClick={() => setEditing(true)}
                  variant="outline"
                  className="w-full mt-3"
                  data-testid="profile-edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" /> Edit Profile
                </Button>
                <Button
                  onClick={() => setShowHRDocs(true)}
                  variant="outline"
                  className="w-full mt-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid="profile-hr-docs-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> My HR Documents
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400 text-sm">Unable to load profile</div>
        )}
      </DialogContent>
      <HRDocumentsModal
        open={showHRDocs}
        onClose={() => setShowHRDocs(false)}
        token={token}
        currentUserId={profile?.id}
      />
    </Dialog>
  );
}
